import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Loader2 } from 'lucide-react';
import { errorMessage } from '../../lib/errors';

interface Props {
    orgId: string | null;
    startDate: string;
    endDate: string;
}

interface KpiData {
    total: number;
}

interface RevenueKpis {
    labour: KpiData;
    parts_cost: KpiData;
    parts_markup: KpiData;
}

// Simple number tile — no sparkline, no variance %. The KPI total is the sum
// over the selected period; the old sparkline reshaped by interval (a flat line
// on "week", a single dot otherwise) added noise without insight, so it — and
// the now-meaningless interval toggle — were removed. Just the figure that
// matters, big and legible.
const KpiCard = ({ title, data, isNegativeCost = false }: { title: string; data?: KpiData; isNegativeCost?: boolean }) => {
    const total = data?.total || 0;
    const display = isNegativeCost ? Math.abs(total) : total;

    return (
        <div className="bg-card border border-line rounded-xl p-6 shadow-xl backdrop-blur-sm flex flex-col justify-between min-h-[128px]">
            <h3 className="text-fg-3 text-sm font-medium mb-4">{title}</h3>
            <div className="text-4xl font-bold font-display text-fg tabular-nums">
                £{display.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
        </div>
    );
};

export const PartsVsLaborChart = ({ orgId, startDate, endDate }: Props) => {
    const [kpiData, setKpiData] = useState<RevenueKpis | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!orgId) return;

        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                // Period totals are interval-independent (the RPC sums all
                // buckets), so a fixed interval is fine now that the sparkline
                // is gone.
                const result = await supabase.rpc('fn_get_revenue_composition_kpis', {
                    p_org_id: orgId,
                    p_interval: 'month',
                    p_start_date: startDate,
                    p_end_date: endDate,
                });

                if (result.error) throw result.error;
                setKpiData(result.data as RevenueKpis);
            } catch (err) {
                console.error('Error fetching KPI data:', err);
                setError(errorMessage(err) || 'Failed to load KPI data');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [orgId, startDate, endDate]);

    return (
        <div className="flex flex-col gap-6">
            <div className="bg-card border border-line rounded-xl p-6 shadow-xl backdrop-blur-sm">
                <h2 className="text-2xl font-bold font-display text-fg mb-2">Revenue Composition</h2>
                <p className="text-fg-3 text-sm">Labour, parts cost, and parts markup for the selected period.</p>
            </div>

            <div className="relative min-h-[128px]">
                {loading && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-scrim/20 backdrop-blur-sm rounded-xl">
                        <Loader2 className="w-8 h-8 text-gold animate-spin" />
                    </div>
                )}

                {error ? (
                    <div className="flex items-center justify-center h-[128px] text-danger bg-card border border-line rounded-xl">
                        {error}
                    </div>
                ) : kpiData ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <KpiCard title="Labour Revenue" data={kpiData.labour} />
                        <KpiCard title="Parts Cost" data={kpiData.parts_cost} isNegativeCost={true} />
                        <KpiCard title="Parts Markup Revenue" data={kpiData.parts_markup} />
                    </div>
                ) : (
                    !loading && (
                        <div className="flex items-center justify-center h-[128px] text-fg-3 bg-card border border-line rounded-xl">
                            No data available for this period.
                        </div>
                    )
                )}
            </div>
        </div>
    );
};
